import React from "react";
import { 
  Container, 
  Grid, 
  Typography, 
  Button, 
  Avatar,
  Stack,
  Divider,
  Box,
  Paper
} from "@mui/material";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart, 
  Pie, 
  Cell 
} from "recharts";
import {
  CalendarToday,
  People,
  Settings,
  Notifications,
  ArrowForward
} from "@mui/icons-material";
import { getGreeting } from "../../utils/GreetingUtils";

// Sample data
const weeklyData = [
  { name: "Mon", appointments: 10 },
  { name: "Tue", appointments: 15 },
  { name: "Wed", appointments: 8 },
  { name: "Thu", appointments: 12 },
  { name: "Fri", appointments: 20 },
  { name: "Sat", appointments: 18 },
];

const patientTypes = [
  { name: "New", value: 15, color: "#4e73df" },
  { name: "Follow-up", value: 35, color: "#1cc88a" },
  { name: "Regular", value: 50, color: "#36b9cc" },
];

const upcomingAppointments = [
  { time: '09:00 AM', patient: 'John Doe', type: 'Follow-up' },
  { time: '10:30 AM', patient: 'Sarah Smith', type: 'New Patient' },
  { time: '02:15 PM', patient: 'Michael Johnson', type: 'Regular' }
];

const DoctorDashboard: React.FC = () => {
  return (
    <>
      
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="600" gutterBottom>
            {getGreeting()}, Dr. Sachin
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>

        {/* Quick Stats */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              icon={<CalendarToday fontSize="large" color="primary" />}
              title="Today's Appointments"
              value="24"
              actionText="View Schedule"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              icon={<People fontSize="large" color="success" />}
              title="Active Patients"
              value="128"
              actionText="Patient List"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              icon={<Notifications fontSize="large" color="warning" />}
              title="Notifications"
              value="3"
              actionText="View Alerts"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              icon={<Settings fontSize="large" color="error" />}
              title="Pending Tasks"
              value="5"
              actionText="Manage Tasks"
            />
          </Grid>
        </Grid>

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Appointments Chart */}
          <Grid item xs={12} md={8}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight="600">
                  Weekly Appointments
                </Typography>
                <Button endIcon={<ArrowForward />} size="small">
                  View All
                </Button>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={weeklyData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="appointments" fill="#4e73df" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Patient Types */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Patient Types
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={patientTypes}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {patientTypes.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} patients`, '']} />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Upcoming Appointments */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight="600">
                  Upcoming Appointments
                </Typography>
                <Button endIcon={<ArrowForward />} size="small">
                  View All
                </Button>
              </Box>
              <Stack spacing={2}>
                {upcomingAppointments.map((appointment, index) => (
                  <React.Fragment key={index}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography fontWeight="500">{appointment.time}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {appointment.patient}
                        </Typography>
                      </Box>
                      <Chip label={appointment.type} color={
                        appointment.type === 'New Patient' ? 'primary' : 
                        appointment.type === 'Follow-up' ? 'success' : 'info'
                      } />
                    </Box>
                    {index < upcomingAppointments.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Stack>
            </Paper>
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Recent Activity
              </Typography>
              <Stack spacing={2}>
                {[
                  "Patient John Doe rescheduled appointment",
                  "New message from Sarah Smith",
                  "System update available",
                  "New patient registration: Emily Wilson"
                ].map((activity, index) => (
                  <React.Fragment key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: index === 0 ? '#4e73df' : '#1cc88a',
                        fontSize: 14 
                      }}>
                        {index + 1}
                      </Avatar>
                      <Typography>{activity}</Typography>
                    </Box>
                    {index < 3 && <Divider />}
                  </React.Fragment>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

// Reusable Stat Card Component
const StatCard = ({ icon, title, value, actionText }: any) => (
  <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
      <Avatar sx={{ bgcolor: 'transparent' }}>
        {icon}
      </Avatar>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
    </Stack>
    <Typography variant="h4" fontWeight="600" sx={{ mb: 2 }}>
      {value}
    </Typography>
    <Button 
      variant="text" 
      size="small" 
      endIcon={<ArrowForward />}
      sx={{ color: 'primary.main', p: 0 }}
    >
      {actionText}
    </Button>
  </Paper>
);

// Reusable Chip Component
const Chip = ({ label, color }: any) => (
  <Box sx={{
    bgcolor: `${color}.light`,
    color: `${color}.dark`,
    px: 1.5,
    py: 0.5,
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500
  }}>
    {label}
  </Box>
);

export default DoctorDashboard;